// Web Speech API wrapper for reading Finn's narrative and question text aloud.
// Klasse 1–2 kids often can't read fluently yet; silent prose is a dealbreaker.
// Enable state is per-device (localStorage). Voice prefers de-DE if available.
// Usage:
//   const { enabled, available, toggle, speak, stop } = useTTS();
//   <button onClick={toggle}>{enabled ? '🔊' : '🔈'}</button>
//   useEffect(() => { if (enabled) speak(narrativeText); }, [exId, enabled]);

import { useCallback, useEffect, useRef, useState } from 'react';

const LS_KEY = 'lernwelt.tts.enabled';

function initialEnabled(): boolean {
  try { return localStorage.getItem(LS_KEY) === '1'; } catch { return false; }
}

function pickGermanVoice(voices: SpeechSynthesisVoice[]): SpeechSynthesisVoice | null {
  if (!voices.length) return null;
  // Prefer de-DE, then any de-*, then default.
  return (
    voices.find(v => v.lang === 'de-DE' && v.localService) ||
    voices.find(v => v.lang === 'de-DE') ||
    voices.find(v => v.lang.startsWith('de')) ||
    null
  );
}

export function useTTS() {
  const available = typeof window !== 'undefined' && 'speechSynthesis' in window;
  const [enabled, setEnabled] = useState<boolean>(available && initialEnabled());
  const voiceRef = useRef<SpeechSynthesisVoice | null>(null);
  const utteranceRef = useRef<SpeechSynthesisUtterance | null>(null);

  // Voices load asynchronously in some browsers.
  useEffect(() => {
    if (!available) return;
    function refreshVoices() {
      voiceRef.current = pickGermanVoice(window.speechSynthesis.getVoices());
    }
    refreshVoices();
    window.speechSynthesis.addEventListener('voiceschanged', refreshVoices);
    return () => window.speechSynthesis.removeEventListener('voiceschanged', refreshVoices);
  }, [available]);

  const stop = useCallback(() => {
    if (!available) return;
    try { window.speechSynthesis.cancel(); } catch { /* ignore */ }
    utteranceRef.current = null;
  }, [available]);

  const speak = useCallback((text: string) => {
    if (!available || !enabled) return;
    const clean = (text || '').trim();
    if (!clean) return;
    // Cancel anything currently queued — we want one utterance at a time.
    try { window.speechSynthesis.cancel(); } catch { /* ignore */ }
    const u = new SpeechSynthesisUtterance(clean);
    u.lang = 'de-DE';
    u.rate = 0.95;           // a touch slower for 7-year-olds
    u.pitch = 1.05;          // slightly warmer
    if (voiceRef.current) u.voice = voiceRef.current;
    utteranceRef.current = u;
    try { window.speechSynthesis.speak(u); } catch { /* ignore */ }
  }, [available, enabled]);

  // Speak regardless of the per-device enable toggle — for explicit
  // listen-buttons (Hör-Aufgaben), where playback IS the exercise.
  const speakAlways = useCallback((text: string) => {
    if (!available) return;
    const clean = (text || '').trim();
    if (!clean) return;
    try { window.speechSynthesis.cancel(); } catch { /* ignore */ }
    const u = new SpeechSynthesisUtterance(clean);
    u.lang = 'de-DE';
    u.rate = 0.9;            // listening exercises: a touch slower still
    u.pitch = 1.05;
    if (voiceRef.current) u.voice = voiceRef.current;
    utteranceRef.current = u;
    try { window.speechSynthesis.speak(u); } catch { /* ignore */ }
  }, [available]);

  const toggle = useCallback(() => {
    setEnabled(prev => {
      const next = !prev;
      try { localStorage.setItem(LS_KEY, next ? '1' : '0'); } catch { /* ignore */ }
      if (!next) {
        try { window.speechSynthesis.cancel(); } catch { /* ignore */ }
      }
      return next;
    });
  }, []);

  // Stop any pending speech on unmount.
  useEffect(() => stop, [stop]);

  return { enabled, available, toggle, speak, speakAlways, stop };
}
